import React, { useState } from "react";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,  } from "recharts";

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload?.length) {
    return (
      <div
        className="rounded-lg border p-3 text-xs"
        style={{ background: "var(--color-card)", borderColor: "var(--color-border)", boxShadow: "var(--shadow-lg)" }}
      >
        <p className="font-heading font-semibold mb-2" style={{ color: "var(--color-foreground)" }}>{label}</p>
        {payload?.map((p) => (
          <div key={p?.dataKey} className="flex items-center gap-2 mb-1">
            <div className="w-2 h-2 rounded-full" style={{ background: p?.color }} />
            <span className="font-caption" style={{ color: "var(--color-text-secondary)" }}>{p?.name}:</span>
            <span className="font-data font-medium" style={{ color: "var(--color-foreground)" }}>{p?.value}</span>
          </div>
        ))}
      </div>
    );
  }
  return null;
};

export default function SatisfactionTrends({ data: dataProp }) {
  const [activeLines, setActiveLines] = useState({ nps: true, csat: true, ces: true });
  const trendData = dataProp || [];

  const toggleLine = (key) => setActiveLines((prev) => ({ ...prev, [key]: !prev?.[key] }));

  const lines = [
    { key: "nps", name: "NPS Score", color: "#1E40AF" },
    { key: "csat", name: "CSAT %", color: "#059669" },
    { key: "ces", name: "CES Score", color: "#D97706" },
  ];

  return (
    <div
      className="rounded-xl border"
      style={{
        background: "var(--color-card)",
        borderColor: "var(--color-border)",
        boxShadow: "var(--shadow-md)",
        padding: "20px",
      }}
    >
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <h3 className="font-heading font-semibold text-base" style={{ color: "var(--color-foreground)" }}>
            Satisfaction Score Trends
          </h3>
          <p className="font-caption text-xs mt-0.5" style={{ color: "var(--color-text-secondary)" }}>
            NPS, CSAT &amp; CES — last 7 months
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {lines?.map((l) => (
            <button
              key={l?.key}
              onClick={() => toggleLine(l?.key)}
              className="flex items-center gap-1.5 px-2 py-1 rounded-full font-caption text-xs transition-all duration-150"
              style={{
                background: activeLines?.[l?.key] ? `${l?.color}18` : "var(--color-muted)",
                color: activeLines?.[l?.key] ? l?.color : "var(--color-text-secondary)",
                border: `1px solid ${activeLines?.[l?.key] ? l?.color : "transparent"}`,
                minHeight: 28,
              }}
            >
              <div className="w-2 h-2 rounded-full" style={{ background: activeLines?.[l?.key] ? l?.color : "var(--color-text-secondary)" }} />
              {l?.name}
            </button>
          ))}
        </div>
      </div>
      <div className="w-full h-48" aria-label="Customer satisfaction trends line chart">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={trendData} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
            <XAxis dataKey="month" tick={{ fontSize: 11, fill: "var(--color-text-secondary)", fontFamily: "var(--font-caption)" }} />
            <YAxis tick={{ fontSize: 11, fill: "var(--color-text-secondary)", fontFamily: "var(--font-caption)" }} />
            <Tooltip content={<CustomTooltip />} />
            {lines?.map((l) =>
              activeLines?.[l?.key] ? (
                <Line
                  key={l?.key}
                  type="monotone"
                  dataKey={l?.key}
                  name={l?.name}
                  stroke={l?.color}
                  strokeWidth={2}
                  dot={{ r: 3, fill: l?.color }}
                  activeDot={{ r: 5 }}
                />
              ) : null
            )}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}